import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import {
  CATEGORIES,
  REGIONS_FOR,
  getCategoryMeta,
  getCategoryMovies,
  filterCatalog,
} from '@/data/catalog';
import type { Movie, SortKey } from '@/types/movie';
import MovieCard from '@/components/base/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterBar, { type FilterValue } from '@/components/feature/FilterBar';
import Pagination from '@/components/feature/Pagination';

const PAGE_SIZE = 20;
const DEFAULT_FILTERS: FilterValue = { region: 'all', year: 'all', sort: 'default' };

export default function Category() {
  const { type = 'movie' } = useParams();
  const meta = getCategoryMeta(type);
  const baseList = useMemo(() => (meta ? getCategoryMovies(type) : []), [meta, type]);

  const [filters, setFilters] = useState<FilterValue>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  }, [type]);

  useDocumentMeta({
    title: meta ? `大雷影视 - ${meta.title}分类_最新电影电视剧推荐` : '分类不存在 - 大雷影视',
    description: meta ? meta.description : '未找到对应的影视分类，请返回大雷影视首页浏览最新电影与电视剧。',
    keywords: meta ? `大雷影视,${meta.title},${meta.title}推荐,影视分类,电影,电视剧` : '大雷影视,影视分类',
  });

  const filtered: Movie[] = useMemo(() => filterCatalog(baseList, filters), [baseList, filters]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleFilterChange = (next: FilterValue) => {
    setFilters(next);
    setPage(1);
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  if (!meta) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 py-16 md:px-8">
        <EmptyState
          title="这个分类暂时不存在"
          description="可能是链接输入有误。你可以返回首页，或从电影、电视剧频道继续浏览。"
          actionLabel="返回首页"
          actionPath="/"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
      <Breadcrumb items={[{ label: '影视分类', path: '/category/movie' }, { label: meta.title }]} />

      <div className="mt-6 border-b border-background-200 pb-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">{meta.title}</h1>
              <span className="mt-1.5 font-label text-xs uppercase tracking-[0.24em] text-foreground-400">
                {meta.enTitle}
              </span>
            </div>
            <p className="mt-2.5 max-w-2xl text-sm leading-relaxed text-foreground-500">{meta.description}</p>
          </div>
          <p className="text-sm text-foreground-400">
            共 <span className="font-label text-lg font-semibold text-accent-600">{filtered.length}</span> 部作品
          </p>
        </div>

        <div className="no-scrollbar mt-6 flex items-center gap-1 overflow-x-auto">
          {CATEGORIES.map((category) => (
            <Link
              key={category.key}
              to={`/category/${category.key}`}
              className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                category.key === type
                  ? 'bg-primary-500 font-medium text-background-50'
                  : 'text-foreground-500 hover:bg-background-100 hover:text-primary-600'
              }`}
            >
              {category.title}
            </Link>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <FilterBar value={filters} onChange={handleFilterChange} regions={REGIONS_FOR(baseList)} onReset={handleReset} />
      </div>

      <div className="mt-7">
        {pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 md:grid-cols-4 md:gap-x-4 lg:grid-cols-5">
              {pageItems.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
            <p className="mt-4 text-center text-xs text-foreground-400">
              演示站点 · 每页展示 {PAGE_SIZE} 条影视内容，共 {totalPages} 页
            </p>
          </>
        ) : (
          <EmptyState
            title="抱歉，暂无符合条件的影视作品"
            description="换个地区或年份试试，也可以重置筛选，看看这个分类下的全部内容。"
            actionLabel="重置筛选条件"
            onAction={handleReset}
          />
        )}
      </div>
    </div>
  );
}